import {
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  injectSlice,
  selectMarketingMeta,
  updateMarketingMeta,
  State,
  appLogout,
} from '@bonanza-social/toolkit/state';

import LandingPromoService from '../../services/LandingPromoService';
import { LandingPromosState } from './landingPromos.types';
import { generateSlug } from '@bonanza-social/toolkit/utils';

export const fetchPromo = createAction('landingPromo/fetch-promos');

export const getPromo = createAsyncThunk(
  'landingPromo/fetch-promos',
  async (_, { dispatch, getState }) => {
    await dispatch(updateMarketingMeta());

    const state = getState() as State;
    const marketingMeta = selectMarketingMeta(state);
    const { promotions } = await LandingPromoService.getLandingPromos({
      params: {
        ReferralCode: marketingMeta.referral_code,
        UtmCampaign: marketingMeta.utm_campaign,
      },
    });

    const landingPromos = promotions.map((promo) => ({
      ...promo,
      slug: generateSlug(promo.title),
    }));

    return landingPromos;
  },
);

const initialState: LandingPromosState = {
  data: [],
  dataState: 'idle',
  isClaimPromoButtonVisible: false,
};

const landingPromo = createSlice({
  name: 'withLandingPromo',
  initialState,
  reducers: {
    setClaimPromoButtonVisibility(state, action) {
      state.isClaimPromoButtonVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPromo.fulfilled, (state, action) => {
      state.dataState = 'fulfilled';
      state.data = action.payload;
    });
    builder.addCase(getPromo.pending, (state) => {
      if (state.dataState !== 'fulfilled') {
        state.dataState = 'pending';
      }
    });
    builder.addCase(getPromo.rejected, (state) => {
      state.dataState = 'rejected';
    });
    builder.addCase(appLogout, () => {
      return initialState;
    });
  },
});

export const { setClaimPromoButtonVisibility } = landingPromo.actions;

export const withLandingPromo = injectSlice({
  reducerPath: 'landingPromo',
  reducer: landingPromo.reducer,
});

export const selectIsClaimPromoButtonVisible = withLandingPromo.selector(
  (state) => state.landingPromo.isClaimPromoButtonVisible,
);

export const selectLandingPromos = withLandingPromo.selector(
  (state) => state.landingPromo.data,
);

export const selectLandingWelcomeGenericPromo = withLandingPromo.selector(
  (state) =>
    state.landingPromo.data.filter(
      (promo) =>
        promo.promoType === 'Welcome' && promo.subPromoType === 'Generic',
    ),
);

export const selectLandingPromosDataState = withLandingPromo.selector(
  (state) => state.landingPromo.dataState,
);

export const selectLandingPromoByType = (promoType?: string) =>
  createSelector(selectLandingPromos, (promos) => {
    if (promoType === 'Welcome') {
      return promos.find(
        (promo) =>
          promo.promoType === 'Welcome' || promo.promoType === 'Referral',
      );
    }
    return promos.find((promo) => promo.promoType === promoType);
  });

declare global {
  interface LazyLoadedSlices {
    landingPromo: ReturnType<typeof landingPromo.reducer>;
  }
}
